/**
 * Typesense `multi_search` answers HTTP 200 even when one of the searches
 * failed (unknown field, bad filter, ...); the failure lives in
 * `results[i].error`. Reading such a result as "no hits" hides the bug, so
 * every multi_search response goes through this helper.
 */

/** One entry of `results` in a multi_search response (loosely typed on purpose). */
export interface MultiSearchResult {
  error?: unknown;
  code?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export class TypesenseSearchError extends Error {
  readonly index: number;
  readonly code?: number;

  constructor(index: number, message: string, code?: number) {
    super(`Typesense multi_search error (search ${index}): ${message}`);
    this.name = "TypesenseSearchError";
    this.index = index;
    this.code = code;
  }
}

/** `multi_search` endpoint URL with optional query params (e.g. use_cache). */
export function buildMultiSearchUrl(
  apiUrl: string,
  queryParams?: Readonly<Record<string, string>>,
): string {
  const query = queryParams ? new URLSearchParams(queryParams).toString() : "";
  return query ? `${apiUrl}${apiUrl.includes("?") ? "&" : "?"}${query}` : apiUrl;
}

/**
 * POST one multi_search and return its results, failed searches thrown.
 * `queryParams` go on the URL: Typesense only honours use_cache / cache_ttl
 * there, and they apply to the whole request.
 */
export async function postMultiSearch(
  config: { apiUrl: string; authHeader: string; apiKey: string },
  searches: Record<string, unknown>[],
  queryParams?: Readonly<Record<string, string>>,
  options?: { timeoutMs?: number },
): Promise<MultiSearchResult[]> {
  const controller = options?.timeoutMs ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), options?.timeoutMs)
    : null;
  try {
    const response = await fetch(
      buildMultiSearchUrl(config.apiUrl, queryParams),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [config.authHeader]: config.apiKey,
        },
        body: JSON.stringify({ searches }),
        ...(controller ? { signal: controller.signal } : {}),
      },
    );
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }
    return getMultiSearchResults(await response.json());
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Return `data.results` after checking every entry for an `error`.
 * Throws a TypesenseSearchError for the first failed search.
 */
export function getMultiSearchResults(data: unknown): MultiSearchResult[] {
  const raw = (data as { results?: unknown } | null | undefined)?.results;
  const results = (Array.isArray(raw) ? raw : []) as MultiSearchResult[];
  results.forEach((result, index) => {
    if (result && typeof result === "object" && result.error) {
      throw new TypesenseSearchError(
        index,
        String(result.error),
        typeof result.code === "number" ? result.code : undefined,
      );
    }
  });
  return results;
}
